/**
 * @file 見開きで隣に並べたページの書き込みを、表示のためだけに読む hook である。
 *
 * @what: (家族, まなぶ人, 資料, ページ) が変わるたびに線を読み直し、線が無ければ null を返す。
 *   書き足しも保存もしない。
 * @why: 見開きでは「いまのページ」(組の先のページ) だけが書き込みの対象になる (書き込む間は1ページで
 *   出す)。隣のページに残した線も見えないと、見開きにしただけで書き込みが消えたように見えるためである。
 * @failure: 読みの失敗は線が無いものとして扱う (表示だけなので書き込みは失わない)。
 */

import { useEffect, useState } from "react";
import { loadPageInk, pageInkPath } from "../lib/pageInk";
import type { HandwritingDoc } from "../model/handwriting";

export function usePageInkView(
  familyId: string,
  learnerId: string | null,
  sourceId: string,
  page: number | null,
  /** 線があると分かっているページか (無いページは読みに行かない)。 */
  inked: boolean,
): HandwritingDoc | null {
  const [loaded, setLoaded] = useState<{ path: string; doc: HandwritingDoc | null } | null>(null);
  const path =
    inked && learnerId !== null && page !== null ? pageInkPath(familyId, learnerId, sourceId, page) : null;

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    loadPageInk(path)
      .then((doc) => {
        if (!cancelled) setLoaded({ path, doc });
      })
      .catch((err: unknown) => {
        console.warn("[study] failed to load page ink for the facing page", err);
        if (!cancelled) setLoaded({ path, doc: null });
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  // 前のページの線を、送った先のページに重ねない
  if (path === null || loaded === null || loaded.path !== path) return null;
  return loaded.doc !== null && loaded.doc.strokes.length > 0 ? loaded.doc : null;
}
